import { parseCSV } from './csv';

const MANAGER_SEASON_FILES = {
  'A.J.': 'AJ_data.csv',
  Alex: 'Alex_data.csv',
  'Harrison Williams': 'Harrison_Williams_data.csv',
  Amir: 'Amir_data.csv',
  Anna: 'Anna_data.csv',
  Blake: 'Blake_data.csv',
  Bryan: 'Bryan_data.csv',
  Josh: 'Josh_data.csv',
  Kyle: 'Kyle_Teichmann_data.csv',
  'Michael R': 'Michael_R_data.csv',
  Robby: 'Robby_data.csv',
  Ryan: 'Ryan_Young_data.csv',
  Samuel: 'Samuel_data.csv',
  Kevin: 'kevin_data.csv',
};

export async function loadManagerSeasons(manager, fetchImpl = fetch) {
  const file = MANAGER_SEASON_FILES[manager];
  if (!file) return [];

  const response = await fetchImpl(`/data/managers/${file}`);
  if (!response.ok) {
    throw new Error(`Failed to load manager season data for ${manager}`);
  }

  const rows = parseCSV(await response.text());
  return rows
    .filter((row) => row.data_type === 'season_detail')
    .map((row) => ({
      year: parseInt(row.year, 10),
      teamName: row.team_name,
      wins: parseInt(row.wins, 10),
      losses: parseInt(row.losses, 10),
      ties: parseInt(row.ties, 10) || 0,
      pointsFor: parseFloat(row.points_for),
      pointsAgainst: parseFloat(row.points_against),
      playoffsMade: row.playoffs_made === 'True',
      gamesPlayed: parseInt(row.games_played, 10),
    }))
    .filter((row) => Number.isFinite(row.year));
}

export function seasonExtremesToBestWorst(manager, seasons, championshipYears = []) {
  if (!seasons || seasons.length === 0) return null;

  const scored = seasons.map((season) => ({
    ...season,
    differential: roundToOne((season.pointsFor || 0) - (season.pointsAgainst || 0)),
  }));

  const best = scored.slice().sort((a, b) => (
    b.wins - a.wins
      || b.pointsFor - a.pointsFor
      || b.differential - a.differential
  ))[0];

  const worst = scored.slice().sort((a, b) => (
    a.wins - b.wins
      || a.pointsFor - b.pointsFor
      || a.differential - b.differential
  ))[0];

  return {
    best: seasonToCard(manager, best, 'best', championshipYears.includes(best.year)),
    worst: seasonToCard(manager, worst, 'worst', championshipYears.includes(worst.year)),
  };
}

function seasonToCard(manager, season, kind, wonTitle) {
  const record = `${season.wins}-${season.losses}${season.ties ? `-${season.ties}` : ''}`;
  const points = formatNumber(season.pointsFor);
  const differential = formatSigned(season.differential);
  const title = kind === 'best'
    ? `${season.year}. ${wonTitle ? 'The ring run.' : 'The high-water mark.'}`
    : `${season.year}. The floor.`;
  const playoffLine = wonTitle
    ? ' It ended with a championship.'
    : season.playoffsMade
      ? ' It still reached the playoff bracket.'
      : ' It missed the playoff bracket.';
  const blurb = kind === 'best'
    ? `${manager} went ${record}, scored ${points} points, and finished ${differential} in point differential.${playoffLine}`
    : `${manager} went ${record}, scored ${points} points, and finished ${differential} in point differential.${playoffLine}`;

  return {
    title,
    blurb,
    record,
    pointsFor: points,
    differential,
  };
}

function formatNumber(value) {
  if (!Number.isFinite(value)) return '-';
  return Math.round(value).toLocaleString('en-US');
}

function formatSigned(value) {
  if (!Number.isFinite(value)) return '-';
  const rounded = Math.round(value);
  return `${rounded >= 0 ? '+' : ''}${rounded.toLocaleString('en-US')}`;
}

function roundToOne(value) {
  return Math.round(value * 10) / 10;
}
